import type { IEnv as IEnvironment } from '../interface/env';
import type { ITranslationStorage } from '../interface/translationStorage';

const DEFAULT_LANGUAGE_CODE = 'en';
const KEY_ENV_TRANSLATIONS_RECORD = 'REACT_APP_TRANSLATIONS';

type LANGUAGE_CODE =
    | 'cs'
    | 'de'
    | 'en'
    | 'es'
    | 'fr'
    | 'it'
    | 'ja'
    | 'ko'
    | 'pl'
    | 'pt'
    | 'ru'
    | 'zh'
    | 'nl'
    | 'ar'
    | 'bp'
    | 'ga'
    | 'gd';

type TYPE_TRANSLATION_STORAGE = Record<string, Partial<Record<LANGUAGE_CODE, string>>>;

const DEFAULT_VALUES: TYPE_TRANSLATION_STORAGE = {
    'Select a language': {
        en: 'Select a language',
        fr: 'Sélectionnez une langue',
        es: 'Seleccione un idioma',
        de: 'Wählen Sie eine Sprache',
        it: 'Selezione lingua',
        ja: '言語選択',
        zh: '请选择语言',
        ko: '언어를 선택하십시오',
        cs: 'Vyberte jazyk',
        nl: 'Kies een taal',
        ru: 'Выберите язык',
        pt: 'Escolha um idioma',
        pl: 'Wybrać język',
    },
    'Choose a tour': {
        en: 'Choose a tour',
        fr: 'Parcours de visite',
        es: 'Seleccione un idioma',
        de: 'Wählen Sie eine Sprache',
        it: 'Selezione lingua',
        ja: '言語選択',
        zh: '请选择语言',
        ko: '언어를 선택하십시오',
        cs: 'Vyberte jazyk',
        pt: 'Escolher uma visita',
        ru: 'Выберите тур',
        pl: 'Wybierz zwiedzanie',
    },
    Timeline: {
        en: 'Timeline',
        fr: 'Frise chronologique',
        es: 'Línea cronológica',
        de: 'Zeitstrahl',
        it: 'Linea del tempo',
        ja: '年表',
        zh: '时间线',
        ko: '연대표',
        cs: 'Časová osa',
        pt: 'Linha do tempo',
        ru: 'Временная шкала',
        pl: 'Oś czasowa',
    },
    Credits: {
        en: 'Credits',
        fr: 'Crédits',
        es: 'Créditos',
        de: 'Copyright',
        it: 'Ringraziamenti',
        ja: 'クレジット',
        zh: '致谢',
        ko: '메뉴',
        cs: 'Titulky',
        pt: 'Créditos',
        ru: 'Контент и права',
        pl: 'Dodatki',
    },
    Menu: {
        en: 'Menu',
        fr: 'Menu',
        es: 'Menú',
        de: 'Menü',
        it: 'Menù',
        ja: '言語',
        zh: '菜单',
        ko: '크레딧',
        cs: 'Nabídka',
        pl: 'Menu',
        pt: 'Menu',
        ru: 'Меню',
    },
    Languages: {
        en: 'Languages',
        fr: 'Langues',
        es: 'Idiomas',
        de: 'Sprachen',
        it: 'Linguas',
        ja: '言語',
        zh: '菜单',
        ko: '언어',
        cs: 'Jazyk',
        pl: 'Język',
        pt: 'Língua',
        ru: 'Язык',
    },
    Tours: {
        en: 'Tours',
        fr: 'Parcours de visite',
        es: 'Visita',
        de: 'Tour',
        it: 'Percorso',
        ja: 'ツアー',
        zh: '旅程',
        ko: '투어',
        cs: 'Prohlídky',
        pt: 'Visita',
        ru: 'Typ',
    },
    Feedback: {
        en: 'Feedback',
        fr: 'Remarques',
        es: 'Comentario',
        de: 'Feedback',
        it: 'Risposta',
        ja: 'フィードバック',
        zh: '反馈',
        ko: '피드백',
        cs: 'Zpětná vazba',
        pl: 'Informacje zwrotne',
        pt: 'Comentários',
        ru: 'Обратная связь',
    },
    Submit: {
        en: 'Submit',
        fr: 'Soumettre',
        es: 'Presentar',
        de: 'Einreichen',
        it: 'OK',
        ja: 'オーケー',
        zh: '遞交',
        ko: '제출하기',
        cs: 'Odevzdat',
        pl: 'Submit',
        pt: 'Está bem',
        ru: 'Отправить',
    },
    'Not now': {
        en: 'Not now',
        fr: 'Pas maintenant',
        es: 'No ahora',
        de: 'Nicht jetzt',
        it: 'No',
        ja: 'ノー',
        zh: '不是現在',
        ko: ' 다음에 하기',
        cs: 'Teď ne',
        pl: 'Not now',
        pt: 'Mais tarde',
        ru: 'Позже',
    },
    'How did we do?': {
        en: 'How did we do?',
        fr: 'Comment avons-nous fait?',
        es: '¿Tus comentarios?',
        de: 'Deine Kommentare?',
        it: 'Come abbiamo fatto?',
        ja: '私たちはどれほどうまくやったのですか？',
        zh: '您的意见',
        ko: '의견이 있으신가요?',
        cs: 'Jak jsme si vedli?',
        pl: 'Sprzężenie zwrotne',
        pt: 'Como fizemos?',
        ru: 'Оцените аудиогид',
    },
    'Thank you!': {
        en: 'Thank you!',
        fr: 'Merci!',
        es: '¡Gracias!',
        de: 'Vielen Dank!',
        it: 'Grazie!',
        ja: 'ありがとう',
        zh: '謝謝！',
        ko: '감사합니다!',
        cs: 'Děkuji!',
        pl: 'Dziękuję Ci',
        pt: 'Obrigado!',
        ru: 'Спасибо!',
    },
    'Please tell us more': {
        en: 'Please tell us more',
        fr: 'Dis nous plus...',
        es: 'Cuentanos más...',
        de: 'Erzählen Sie uns mehr...',
        it: 'Per favore dicci di più',
        ja: 'もっと教えてください',
        zh: '請告訴我們更多...',
        ko: ' 더 자세하게 작성해 주세요',
        cs: 'Sdělte nám více prosím',
        pl: 'Please tell us more',
        pt: 'Por favor nos conte mais',
        ru: 'Пожалуйста, расскажите нам больше',
    },
    'Give feedback': {
        en: 'Give feedback',
        fr: 'Donner des commentaires?',
        es: '¿Dar opinion?',
        de: 'Feedback geben',
        it: 'Risposta',
        ja: 'フィードバック',
        zh: '給予反饋？',
        ko: '피드백 제출하기',
        cs: 'Dejte nám zpětnou vazbu.',
        pl: 'Przekaż opinię',
        pt: 'Dê retorno',
        ru: 'Оставить отзыв',
    },
    'Tell us your thoughts': {
        en: 'Tell us your thoughts',
        fr: 'Dis nous tes pensées',
        es: 'Cuéntanos tus pensamientos',
        de: 'Sag uns deine Gedanken',
        it: 'Diteci i vostri pensieri',
        ja: 'あなたの考えを教えてください',
        zh: '告訴我們你的想法',
        ko: '의견을 알려주세요',
        cs: 'Řekněte nám, co si myslíte',
        pl: 'Tell us your thoughts',
        pt: 'O que você acha',
        ru: 'Ваш отзыв',
    },
    Cancel: {
        en: 'Cancel',
        fr: 'Annuler',
        es: 'Cancelar',
        de: 'Stornieren',
        it: 'Cancellare',
        ja: 'キャンセル',
        zh: '取消',
        ko: '취소',
        cs: 'Zrušit',
        pl: 'Cancel',
        pt: 'Cancelar',
        ru: 'Отмена',
    },
    'Please connect your headphones': {
        ar: 'توصيل سماعات الرأس',
        en: 'Please connect your headphones',
        fr: 'Veuillez brancher votre casque',
        es: 'Conecte sus auriculares',
        de: 'Bitte schließen Sie Ihre Kopfhörer an',
        it: 'Collegare le cuffie',
        ja: 'ヘッドフォンを接続してください。',
        nl: 'Please connect your headphones',
        zh: '请插入耳机',
        ko: 'Please connect your headphones',
        cs: 'Please connect your headphones',
        pl: 'Please connect your headphones',
        bp: 'Please connect your headphones',
        ru: 'Пожалуйста, подключите наушники',
        ga: 'Cuir do chluas le do thoil',
        gd: 'Ceangail na cluais-làimhe agad',
        pt: 'Por favor, conecte seus fones de ouvido',
    },
    'WiFi may not be consistently available on site at %@. We recommend downloading before your visit. %@ space required':
        {
            en: 'WiFi may not be consistently available on site at %@. We recommend downloading before your visit. %@ space required',
            fr: 'Pour utiliser l’appli au château de %@, merci de la télécharger avant votre visite. %@ space required',
            de: 'Um die App im %@ zu benutzen, laden Sie sie bitte vor Ihrem Besuch runter. %@ space required',
            es: 'Para poder usar la aplicación del %@, descárguesela antes de la visita. %@ space required',
            ru: 'Чтобы использовать мобильное приложение в %@, пожалуйста, скачайте его перед вашим визитом. %@ space required',
            zh: '为了更好地体验 %@，请在您参观之前下载解说app. %@ space required',
            ja: '%@ 城でアプリをご使用になるには、ご訪問の前にダウンロードしていただきますよう、お願いいたします。%@ space required',
            nl: 'WiFi may not consistently available on site at %@. We recommend downloading before your visit. %@ space required',
        },
    Download: {
        en: 'Download',
        fr: 'Téléchargement',
        de: 'Runterladen',
        es: 'Descargar',
        ru: 'Скачать',
        zh: '下载',
        ja: 'ダウンロード',
        nl: 'Download',
    },
    'Download completed': {
        en: 'Download completed',
        fr: 'Téléchargement terminé',
        de: 'Download abgeschlossen',
        es: 'Descarga completada',
        ru: 'Загрузка завершена',
        zh: '下载完成',
        ja: 'ダウンロード完了',
        nl: 'Download completed',
    },
    'Ask Later': {
        en: 'Ask Later',
    },
};

function TranslationStorage(environment: IEnvironment): ITranslationStorage {
    const controller: ITranslationStorage = {
        translation: (
            language: LANGUAGE_CODE | undefined,
            key: string,
            parameters: string[] = [],
            fallback = '',
        ) => {
            const translationOverrides = environment.getValueWithDefault<
                Partial<TYPE_TRANSLATION_STORAGE>
            >(KEY_ENV_TRANSLATIONS_RECORD, { [key]: {} });

            const languageLC = (language ?? DEFAULT_LANGUAGE_CODE).toLowerCase() as LANGUAGE_CODE;
            const languageUC = (language ?? DEFAULT_LANGUAGE_CODE).toUpperCase() as LANGUAGE_CODE;
            const keyLookup3 = `${key}-${languageLC}`;
            const keyLookup4 = `${key}-${languageUC}`;

            /* Go through each option, & if no result is found, fallback to next line, until if nothing is found use value #fallback */
            let returnValue =
                (translationOverrides[key]?.[languageLC] ?? '') /* Prioritize values from ENV */ ||
                (translationOverrides[key]?.[languageUC] ?? '') ||
                environment.getString(keyLookup3) /* Prioritize values with specified language */ ||
                environment.getString(keyLookup4) ||
                (translationOverrides[key]?.[DEFAULT_LANGUAGE_CODE] ?? '') ||
                environment.getString(`${key}-${DEFAULT_LANGUAGE_CODE}`) ||
                (DEFAULT_VALUES[key]?.[languageLC] ?? '') ||
                (DEFAULT_VALUES[key]?.[DEFAULT_LANGUAGE_CODE] ?? '') ||
                fallback ||
                key;

            /* Go through the parameters & apply swapping out of keywords, where a given keyword is wrapped in '%' quotes */
            for (const insValue of parameters) {
                returnValue = returnValue.replace(`%@`, insValue);
            }

            return returnValue;
        },
    };

    return controller;
}

export default TranslationStorage;
